import React from 'react';
import { TablePagination } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';


interface IResultsTable {
  results: Array<IBulkPriceResultChunk>;
  page: number;
  totalCount: number;
  limit: number;
  // eslint-disable-next-line no-unused-vars
  onPageChange: (page: number) => void;
}

const STATUS_COLORS: Record<string, string> = {
  updated: 'bg-success text-white',
  skipped: 'bg-warning text-white',
  not_found: 'bg-error text-white',
  brand_mismatch: 'bg-light-gray',
  error: 'bg-error text-white'
};

// A rule per row, and cells pinned to the top: one SKU can carry five interval lines, and centred
// cells leave its price floating half a row away from the SKU it belongs to.
const useStyles = makeStyles(() => ({
  table: {
    borderCollapse: 'collapse',
    '& tbody td': {
      borderTop: '1px solid rgb(224, 224, 224)',
      verticalAlign: 'top'
    }
  }
}));

const formatPrice = (value: number | null) => (value === null || value === undefined ? '-' : value);

// Same wording as the product subscription page, so a result row and the product screen call the
// same interval the same thing. A count of 0 is the one time entry.
const describeInterval = ({ frequencyCount, frequencyUnit }: IBulkPriceInterval) => {
  const count = Number(frequencyCount) || 0;

  if (!count) {
    return 'One Time Purchase';
  }

  // The plural hangs off the unit, so a missing unit degrades to "Every 3", never "Every 3 s".
  const unit = frequencyUnit || '';

  return `Every${count === 1 ? '' : ` ${count}`} ${unit}${unit && count > 1 ? 's' : ''}`.trim();
};

// `empty` differs by side: a missing old price never existed, a missing new one was cleared.
const formatInstallment = (value: number | null, empty: string) => (
  value === null || value === undefined ? empty : Number(value).toFixed(2)
);

// A one time interval that had no price of its own and is written NULL again. A real write, and
// logged as one, but nothing about the product changed - so it is not shown, or it would bury the
// intervals that did change under a line that always says the same thing.
const isUnchangedOneTime = ({ frequencyCount, oldInstallmentPrice }: IBulkPriceInterval) => (
  !Number(frequencyCount) && (oldInstallmentPrice === null || oldInstallmentPrice === undefined)
);

const ResultsTable = ({ results, page, totalCount, limit, onPageChange }: IResultsTable) => {
  const classes = useStyles();

  const handleChangePage = (event, nextPage) => {
    onPageChange(nextPage + 1);
  };

  // text-left because the admin tab's Card is text-center, which would otherwise centre every
  // cell under a left aligned header. RunProgress does the same.
  return (
    <div className="mt-4 text-left">
      {/* Wide table, so it scrolls inside its own container rather than the page */}
      <div className="scroll-x">
        <table className={`w-full text-13 ${classes.table}`}>
          <thead>
            <tr>
              <th className="text-left p-1">Store</th>
              <th className="text-left p-1">SKU</th>
              <th className="text-left p-1">Status</th>
              <th className="text-left p-1">Old</th>
              <th className="text-left p-1">New</th>
              <th className="text-left p-1">Reason</th>
              <th className="text-left p-1">Subscription update</th>
            </tr>
          </thead>
          <tbody>
            {results.map((chunk) => {
              if (chunk.error?.kind) {
                return (chunk.error.skus || []).map((sku) => (
                  <tr key={`${chunk._id}-${sku}`} className="bg-error text-white">
                    <td className="p-1">{chunk.storeLabel}</td>
                    <td className="p-1">{sku}</td>
                    <td className="p-1">error</td>
                    <td className="p-1">-</td>
                    <td className="p-1">-</td>
                    <td className="p-1" colSpan={2}>{chunk.error.kind}: {chunk.error.message}</td>
                  </tr>
                ));
              }

              return chunk.items.map((item) => (
                <tr key={`${chunk._id}-${item.sku}`}>
                  <td className="p-1">{chunk.storeLabel}</td>
                  <td className="p-1">{item.sku}</td>
                  <td className="p-1">
                    <span className={`px-1 border-radius-4 ${STATUS_COLORS[item.status] || ''}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="p-1">{formatPrice(item.oldPrice)}</td>
                  <td className="p-1">{formatPrice(item.newPrice)}</td>
                  <td className="p-1">{item.reason}</td>
                  {/* One line per interval, named. The endpoint's own sentence repeats the same
                      "Old price X. Updated price: Y" per interval and names none of them. */}
                  <td className="p-1">
                    {(item.intervals || [])
                      .filter((interval) => !isUnchangedOneTime(interval))
                      .map((interval, intervalIndex) => (
                        <div key={interval.intervalId || intervalIndex} className="whitespace-no-wrap">
                          {describeInterval(interval)}: {formatInstallment(interval.oldInstallmentPrice, '-')}
                          {' → '}{formatInstallment(interval.newInstallmentPrice, 'cleared')}
                          {Boolean(interval.note) && Boolean(Number(interval.frequencyCount)) &&
                            <span className="text-muted"> ({interval.note})</span>
                          }
                        </div>
                      ))}
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={totalCount}
        page={page - 1}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
        onPageChange={handleChangePage}
        labelRowsPerPage="Batches per page"
      />
    </div>
  );
};

export default React.memo(ResultsTable);
